// src/features/dashboard/components/ManagerDashboard/IdleTimeReport/DailyIdleTrendChart.tsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from 'recharts';

import type {
  IdleTrendResponse,
} from '../../../types/Manager/idleTime.types';

/* ============================================================
   TYPES
============================================================ */

interface Props {
  data: IdleTrendResponse[];
}

type TrendRow = IdleTrendResponse & {
  teamLeadName?: string;
  teamLeadCode?: string;
};

interface LegendItem {
  value: string;
  color: string;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: readonly any[];
  label?: string;
}

/* ============================================================
   CONSTANTS
============================================================ */

const TEAM_COLORS = [
  '#EF4444',
  '#F97316',
  '#F59E0B',
  '#22C55E',
  '#3B82F6',
  '#8B5CF6',
  '#EC4899',
  '#14B8A6',
  '#6366F1',
  '#84CC16',
];

const TARGET_PERCENTAGE = 14;

const MAX_Y_VALUE = 30;

const CHART_HEIGHT = 280;

const Y_AXIS_WIDTH = 48;

const X_AXIS_HEIGHT = 28;

const DATE_COLUMN_WIDTH = 72;

/*
 * These margins are deliberately small.
 *
 * The chart must use the complete available width.
 */
const CHART_MARGIN = {
  top: 10,
  right: 4,
  bottom: 20,
  left: 0,
};

/* ============================================================
   TOOLTIP
============================================================ */

const CustomTooltip = ({
  active,
  payload,
  label,
}: CustomTooltipProps) => {
  if (
    !active ||
    !payload ||
    payload.length === 0
  ) {
    return null;
  }

  return (
    <div
      className="
        w-[220px]
        max-w-[calc(100vw-32px)]
        overflow-hidden
        rounded-xl
        border
        border-slate-200
        bg-white
        shadow-xl
      "
    >
      <div
        className="
          border-b
          border-slate-200
          bg-slate-50
          px-3
          py-2
        "
      >
        <p
          className="
            truncate
            text-xs
            font-semibold
            text-slate-800
          "
        >
          {label}
        </p>
      </div>

      <div className="space-y-2 p-3">
        {payload.map(
          (item, index) => {
            const value = Number(
              item?.value ?? 0,
            );

            const color =
              item?.color ??
              '#94A3B8';

            return (
              <div
                key={`${item?.dataKey}-${index}`}
                className="
                  flex
                  min-w-0
                  items-center
                  justify-between
                  gap-3
                "
              >
                <div
                  className="
                    flex
                    min-w-0
                    items-center
                    gap-2
                  "
                >
                  <span
                    className="
                      h-2.5
                      w-2.5
                      shrink-0
                      rounded-full
                    "
                    style={{
                      backgroundColor:
                        color,
                    }}
                  />

                  <span
                    className="
                      min-w-0
                      truncate
                      text-[11px]
                      font-medium
                      text-slate-600
                    "
                  >
                    {item?.name ??
                      item?.dataKey}
                  </span>
                </div>

                <span
                  className="
                    shrink-0
                    text-[11px]
                    font-semibold
                    text-slate-800
                  "
                >
                  {value.toFixed(1)}%
                </span>
              </div>
            );
          },
        )}
      </div>
    </div>
  );
};

/* ============================================================
   LEGEND
============================================================ */

interface CustomLegendProps {
  payload?: readonly LegendItem[];

  hoveredTeam: string | null;

  onHoverTeam: (
    team: string | null,
  ) => void;
}

const CustomLegend = ({
  payload,
  hoveredTeam,
  onHoverTeam,
}: CustomLegendProps) => {
  if (
    !payload ||
    payload.length === 0
  ) {
    return null;
  }

  return (
    <div
      className="
        flex
        w-full
        min-w-0
        flex-wrap
        items-center
        justify-center
        gap-x-4
        gap-y-1
        px-1
        pb-2
      "
    >
      {payload.map(
        (item, index) => {
          const teamName =
            item.value;

          const isHovered =
            hoveredTeam ===
            teamName;

          const hasHover =
            hoveredTeam !== null;

          const isFaded =
            hasHover &&
            !isHovered;

          return (
            <button
              key={`${teamName}-${index}`}
              type="button"
              className="
                flex
                min-w-0
                max-w-full
                cursor-pointer
                items-center
                gap-1.5
                rounded-md
                px-1
                py-1
                outline-none
              "
              style={{
                opacity: isFaded
                  ? 0.35
                  : 1,
              }}
              onMouseEnter={() =>
                onHoverTeam(
                  teamName,
                )
              }
              onMouseLeave={() =>
                onHoverTeam(null)
              }
            >
              <span
                className="
                  h-2.5
                  w-2.5
                  shrink-0
                  rounded-full
                "
                style={{
                  backgroundColor:
                    item.color,
                  transform:
                    isHovered
                      ? 'scale(1.2)'
                      : 'scale(1)',
                }}
              />

              <span
                className="
                  max-w-[160px]
                  truncate
                  text-[11px]
                  font-medium
                "
                style={{
                  color: isHovered
                    ? item.color
                    : isFaded
                      ? '#94A3B8'
                      : '#475569',

                  fontWeight:
                    isHovered
                      ? 700
                      : 500,
                }}
              >
                {teamName}
              </span>
            </button>
          );
        },
      )}
    </div>
  );
};

/* ============================================================
   MAIN COMPONENT
============================================================ */

const DailyIdleTrendChart = ({
  data,
}: Props) => {
  /* ==========================================================
     REFS
  ========================================================== */

  const chartOuterRef =
    useRef<HTMLDivElement | null>(
      null,
    );

  const scrollRef =
    useRef<HTMLDivElement | null>(
      null,
    );

  /* ==========================================================
     STATE
  ========================================================== */

  const [
    containerWidth,
    setContainerWidth,
  ] = useState(0);

  const [
    hoveredTeam,
    setHoveredTeam,
  ] = useState<string | null>(
    null,
  );

  const [
    isDragging,
    setIsDragging,
  ] = useState(false);

  /* ==========================================================
     DRAG REFS
  ========================================================== */

  const dragStartX =
    useRef(0);

  const dragScrollLeft =
    useRef(0);

  /* ==========================================================
     RESPONSIVE WIDTH

     ResizeObserver handles:

       100%
       110%
       125%
       150%

     without requiring a page refresh.
  ========================================================== */

  useEffect(() => {
    const element =
      chartOuterRef.current;

    if (!element) {
      return;
    }

    const updateWidth = () => {
      const width =
        element.clientWidth;

      if (width > 0) {
        setContainerWidth(
          width,
        );
      }
    };

    updateWidth();

    const observer =
      new ResizeObserver(() => {
        updateWidth();
      });

    observer.observe(element);

    window.addEventListener(
      'resize',
      updateWidth,
      {
        passive: true,
      },
    );

    return () => {
      observer.disconnect();

      window.removeEventListener(
        'resize',
        updateWidth,
      );
    };
  }, []);

  /* ==========================================================
     NORMALIZE DATA
============================================================ */

  const normalizedData =
    useMemo<TrendRow[]>(() => {
      if (!Array.isArray(data)) {
        return [];
      }

      return data
        .filter(Boolean)
        .map((item) => {
          const row =
            item as TrendRow;

          return {
            ...row,

            teamLeadName:
              row.teamLeadName ??
              'Team',
          };
        });
    }, [data]);

  /* ==========================================================
     TEAMS
============================================================ */

  const teams = useMemo(
    () => {
      const teamSet =
        new Set<string>();

      normalizedData.forEach(
        (item) => {
          const teamName =
            String(
              item.teamLeadName ??
                'Team',
            ).trim();

          if (teamName) {
            teamSet.add(
              teamName,
            );
          }
        },
      );

      return Array.from(
        teamSet,
      );
    },
    [normalizedData],
  );

  /* ==========================================================
     DATES
============================================================ */

  const dates = useMemo(
    () => {
      const dateSet =
        new Set<string>();

      normalizedData.forEach(
        (item) => {
          if (!item.loggedDate) {
            return;
          }

          dateSet.add(
            String(
              item.loggedDate,
            ).slice(0, 10),
          );
        },
      );

      return Array.from(
        dateSet,
      ).sort();
    },
    [normalizedData],
  );

  /* ==========================================================
     CHART DATA
============================================================ */

  const chartData = useMemo(
    () => {
      return dates.map(
        (date) => {
          const row: Record<
            string,
            string | number
          > = {
            date,
          };

          teams.forEach(
            (team) => {
              const item =
                normalizedData.find(
                  (x) =>
                    String(
                      x.loggedDate,
                    ).slice(
                      0,
                      10,
                    ) === date &&
                    (
                      x.teamLeadName ??
                      'Team'
                    ) === team,
                );

              row[team] =
                Number(
                  item?.idlePercentage ??
                    0,
                );
            },
          );

          return row;
        },
      );
    },
    [
      dates,
      teams,
      normalizedData,
    ],
  );

  /* ==========================================================
     DATE FORMAT
============================================================ */

  const formatDate = useCallback(
    (value: string) => {
      if (!value) {
        return '';
      }

      const date =
        new Date(
          `${value}T00:00:00`,
        );

      if (
        Number.isNaN(
          date.getTime(),
        )
      ) {
        return value;
      }

      const day =
        String(
          date.getDate(),
        ).padStart(2, '0');

      const month =
        date.toLocaleString(
          'en-US',
          {
            month: 'short',
          },
        );

      return `${day} ${month}`;
    },
    [],
  );

  /* ==========================================================
     DRAG START
============================================================ */

  const handleMouseDown =
    useCallback(
      (
        event: React.MouseEvent<HTMLDivElement>,
      ) => {
        const element =
          scrollRef.current;

        if (!element) {
          return;
        }

        if (
          element.scrollWidth <=
          element.clientWidth
        ) {
          return;
        }

        setIsDragging(true);

        dragStartX.current =
          event.clientX;

        dragScrollLeft.current =
          element.scrollLeft;
      },
      [],
    );

  /* ==========================================================
     DRAG MOVE
============================================================ */

  const handleMouseMove =
    useCallback(
      (
        event: React.MouseEvent<HTMLDivElement>,
      ) => {
        if (!isDragging) {
          return;
        }

        const element =
          scrollRef.current;

        if (!element) {
          return;
        }

        const distance =
          event.clientX -
          dragStartX.current;

        element.scrollLeft =
          dragScrollLeft.current -
          distance;
      },
      [isDragging],
    );

  /* ==========================================================
     STOP DRAGGING
============================================================ */

  const stopDragging =
    useCallback(() => {
      setIsDragging(false);
    }, []);

  /* ==========================================================
     MOUSE WHEEL

     Horizontal scroll is used only when
     there is actual overflow.
  ========================================================== */

  useEffect(() => {
    const element =
      scrollRef.current;

    if (!element) {
      return;
    }

    const handleWheel = (
      event: WheelEvent,
    ) => {
      if (
        element.scrollWidth <=
        element.clientWidth + 1
      ) {
        return;
      }

      const delta =
        Math.abs(
          event.deltaY,
        ) >
        Math.abs(
          event.deltaX,
        )
          ? event.deltaY
          : event.deltaX;

      if (delta === 0) {
        return;
      }

      event.preventDefault();

      element.scrollLeft +=
        delta;
    };

    element.addEventListener(
      'wheel',
      handleWheel,
      {
        passive: false,
      },
    );

    return () => {
      element.removeEventListener(
        'wheel',
        handleWheel,
      );
    };
  }, [
    chartData.length,
  ]);

  /* ==========================================================
     EMPTY DATA
============================================================ */

  if (
    !normalizedData.length ||
    !chartData.length
  ) {
    return (
      <div
        className="
          flex
          min-h-[280px]
          w-full
          max-w-full
          min-w-0
          items-center
          justify-center
          overflow-hidden
          rounded-xl
          border
          border-slate-200
          bg-white
        "
      >
        <div className="px-4 text-center">
          <p
            className="
              text-sm
              font-semibold
              text-slate-700
            "
          >
            No Idle Trend Data Available
          </p>

          <p
            className="
              mt-1
              text-xs
              text-slate-400
            "
          >
            No daily idle percentage
            data was returned for
            the selected period.
          </p>
        </div>
      </div>
    );
  }

  /* ==========================================================
     REQUIRED WIDTH

     ONE DAY:
       Always use 100% of the card.

     MULTIPLE DAYS:
       Use minimum width per date only when
       necessary.
  ========================================================== */

  const oneDay =
    dates.length <= 1;

  const dateBasedWidth =
    dates.length *
    DATE_COLUMN_WIDTH;

  const actualChartWidth =
    oneDay
      ? Math.max(
          containerWidth,
          1,
        )
      : Math.max(
          containerWidth,
          dateBasedWidth,
        );

  /* ==========================================================
     Y TICKS
============================================================ */

  const yTicks = [
    0,
    5,
    10,
    15,
    20,
    25,
    30,
  ];

  /* ==========================================================
     LEGEND
============================================================ */

  const legendPayload: LegendItem[] =
    teams.map(
      (team, index) => ({
        value: team,

        color:
          TEAM_COLORS[
            index %
              TEAM_COLORS.length
          ],
      }),
    );

  /* ==========================================================
     TARGET POSITION

     This is only used for the fixed target badge.
  ============================================================= */

  const plotTop =
    CHART_MARGIN.top;

  const plotBottom =
    CHART_HEIGHT -
    CHART_MARGIN.bottom -
    X_AXIS_HEIGHT;

  const plotHeight =
    plotBottom -
    plotTop;

  const targetTop =
    plotTop +
    (
      (MAX_Y_VALUE -
        TARGET_PERCENTAGE) /
      MAX_Y_VALUE
    ) *
      plotHeight;

  /* ==========================================================
     RENDER
============================================================ */

  return (
    <div
      className="
        box-border
        w-full
        max-w-full
        min-w-0
        overflow-hidden
        rounded-xl
        border
        border-slate-200
        bg-white
      "
    >
      {/* ======================================================
          HEADER
      ====================================================== */}

      <div
        className="
          box-border
          flex
          w-full
          min-w-0
          flex-wrap
          items-center
          justify-between
          gap-2
          px-4
          pt-3
        "
      >
        <div
          className="
            flex
            min-w-0
            max-w-full
            flex-wrap
            items-center
            gap-2
          "
        >
          <h3
            className="
              shrink-0
              text-[14px]
              font-semibold
              leading-5
              text-slate-800
            "
          >
            Daily Idle % Trend
          </h3>

          <span
            className="
              shrink-0
              rounded-full
              bg-blue-50
              px-2.5
              py-1
              text-[9px]
              font-medium
              leading-3
              text-blue-600
            "
          >
            WTD · by team · target
            &lt;{TARGET_PERCENTAGE}%
          </span>
        </div>

        <span
          className="
            shrink-0
            text-[10px]
            font-medium
            leading-4
            text-slate-400
          "
        >
          {dates.length} days
        </span>
      </div>

      {/* ======================================================
          LEGEND
      ====================================================== */}

      <div
        className="
          box-border
          w-full
          min-w-0
          px-4
          pt-2
        "
      >
        <CustomLegend
          payload={
            legendPayload
          }
          hoveredTeam={
            hoveredTeam
          }
          onHoverTeam={
            setHoveredTeam
          }
        />
      </div>

      {/* ======================================================
          COMPLETE CHART ROW

          THIS IS THE IMPORTANT PART.

          The outer container is explicitly 100%.

          Y axis is overlayed.

          Chart itself is also explicitly 100%.
      ====================================================== */}

      <div
        ref={chartOuterRef}
        className="
          relative
          box-border
          block
          h-[280px]
          w-full
          min-w-0
          max-w-full
          overflow-hidden
          px-3
          pb-3
        "
      >
        {/* ====================================================
            CHART SCROLL AREA
        ==================================================== */}

        <div
          ref={scrollRef}
          className={`
            daily-idle-scroll
            absolute
            inset-x-3
            top-0
            bottom-3
            overflow-x-auto
            overflow-y-hidden
            ${
              isDragging
                ? 'cursor-grabbing'
                : 'cursor-grab'
            }
          `}
          style={{
            scrollbarWidth:
              'none',
            msOverflowStyle:
              'none',
          }}
          onMouseDown={
            handleMouseDown
          }
          onMouseMove={
            handleMouseMove
          }
          onMouseUp={
            stopDragging
          }
          onMouseLeave={
            stopDragging
          }
        >
          {/* ==================================================
              FULL WIDTH CHART

              IMPORTANT:
              For 1 day:

                width = 100%

              Therefore:

                Target line
                Grid lines
                X-axis

              all reach the COMPLETE row.
          ================================================== */}

          <div
            className="
              relative
              box-border
              h-full
            "
            style={{
              width:
                `${actualChartWidth}px`,

              /*
               * Never let one-day chart become
               * smaller than its parent.
               */
              minWidth:
                oneDay
                  ? '100%'
                  : `${actualChartWidth}px`,
            }}
          >
            <ResponsiveContainer
              width="100%"
              height="100%"
              minWidth={0}
              minHeight={0}
            >
              <LineChart
                data={chartData}
                width={
                  undefined
                }
                height={
                  undefined
                }
                margin={{
                  top:
                    CHART_MARGIN.top,
                  right:
                    CHART_MARGIN.right,
                  bottom:
                    CHART_MARGIN.bottom,
                  left:
                    Y_AXIS_WIDTH,
                }}
              >
                {/* ==============================================
                    GRID
                ============================================== */}

                <CartesianGrid
                  vertical={false}
                  horizontal={true}
                  stroke="#E2E8F0"
                  strokeDasharray="3 4"
                />

                {/* ==============================================
                    X AXIS

                    This line now goes to the full chart width.
                ============================================== */}

                <XAxis
                  dataKey="date"
                  axisLine={{
                    stroke:
                      '#CBD5E1',
                    strokeWidth: 1,
                  }}
                  tickLine={false}
                  interval={0}
                  tick={{
                    fontSize: 10,
                    fill: '#475569',
                  }}
                  tickFormatter={
                    formatDate
                  }
                  minTickGap={10}
                  height={
                    X_AXIS_HEIGHT
                  }
                />

                {/* ==============================================
                    Y AXIS

                    Hidden visually.
                    Labels are handled separately.
                ============================================== */}

                <YAxis
                  hide
                  domain={[
                    0,
                    MAX_Y_VALUE,
                  ]}
                  ticks={yTicks}
                />

                {/* ==============================================
                    TARGET LINE

                    IMPORTANT:

                    Because LineChart itself is now 100%
                    of the complete row, ReferenceLine
                    also reaches the complete row.
                ============================================== */}

                <ReferenceLine
                  y={
                    TARGET_PERCENTAGE
                  }
                  stroke="#EF4444"
                  strokeWidth={1.5}
                  strokeDasharray="5 5"
                  ifOverflow="extendDomain"
                />

                {/* ==============================================
                    TOOLTIP
                ============================================== */}

                <Tooltip
                  content={
                    <CustomTooltip />
                  }
                  cursor={{
                    stroke:
                      '#CBD5E1',
                    strokeWidth: 1,
                    strokeDasharray:
                      '4 4',
                  }}
                />

                {/* ==============================================
                    TEAM LINES
                ============================================== */}

                {teams.map(
                  (
                    team,
                    index,
                  ) => {
                    const color =
                      TEAM_COLORS[
                        index %
                          TEAM_COLORS.length
                      ];

                    const isHovered =
                      hoveredTeam ===
                      team;

                    const hasHover =
                      hoveredTeam !==
                      null;

                    const opacity =
                      !hasHover ||
                      isHovered
                        ? 1
                        : 0.15;

                    return (
                      <Line
                        key={team}
                        type="natural"
                        dataKey={
                          team
                        }
                        name={
                          team
                        }
                        stroke={
                          color
                        }
                        strokeWidth={
                          isHovered
                            ? 3
                            : 2
                        }
                        strokeOpacity={
                          opacity
                        }
                        dot={{
                          r: isHovered
                            ? 4
                            : 3,
                          strokeWidth:
                            1.5,
                          fill:
                            color,
                          opacity,
                        }}
                        activeDot={{
                          r: isHovered
                            ? 6
                            : 5,
                          stroke:
                            '#FFFFFF',
                          strokeWidth:
                            2,
                          fill:
                            color,
                        }}
                        connectNulls
                        isAnimationActive={
                          false
                        }
                      />
                    );
                  },
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* ====================================================
            Y AXIS LABELS

            Overlay only.
            They DON'T consume chart width.
        ==================================================== */}

        <div
          className="
            pointer-events-none
            absolute
            inset-y-0
            left-3
            z-30
            bg-white
          "
          style={{
            width:
              Y_AXIS_WIDTH,
          }}
        >
          {yTicks.map(
            (tick) => {
              const top =
                plotTop +
                (
                  (MAX_Y_VALUE -
                    tick) /
                  MAX_Y_VALUE
                ) *
                  plotHeight;

              return (
                <span
                  key={tick}
                  className="
                    absolute
                    right-2
                    -translate-y-1/2
                    whitespace-nowrap
                    text-[10px]
                    font-medium
                    leading-none
                    text-slate-500
                  "
                  style={{
                    top,
                  }}
                >
                  {tick}%
                </span>
              );
            },
          )}
        </div>

        {/* ====================================================
            TARGET BADGE

            Positioned at the COMPLETE ROW's right edge.
        ==================================================== */}

        <div
          className="
            pointer-events-none
            absolute
            right-3
            z-50
            -translate-y-1/2
          "
          style={{
            top: targetTop,
          }}
        >
          <div
            className="
              flex
              h-[20px]
              min-w-[68px]
              items-center
              justify-center
              rounded-[4px]
              border
              border-red-300
              bg-white
              px-1.5
              shadow-sm
            "
          >
            <span
              className="
                whitespace-nowrap
                text-[10px]
                font-medium
                leading-none
                text-red-500
              "
            >
              Target {TARGET_PERCENTAGE}%
            </span>
          </div>
        </div>
      </div>

      {/* ========================================================
          HIDE SCROLLBAR
      ======================================================== */}

      <style>
        {`
          .daily-idle-scroll::-webkit-scrollbar {
            width: 0;
            height: 0;
            display: none;
          }

          .daily-idle-scroll {
            scrollbar-width: none;
            -ms-overflow-style: none;
            -webkit-overflow-scrolling: touch;
          }
        `}
      </style>
    </div>
  );
};

export default DailyIdleTrendChart;